import { AddAffordance, EmptyState, SectionCard } from '@atlas/ui-components'
import { ASSERTION_HINT, ChipSet, TupleChipSet, type TupleSelection } from '@/components/classification/ChipSet'

export interface ClassificationPanelProps {
	/** axis code -> value labels, e.g. `{ brand: ['Bravecto'], geography: ['Spain'] }`. */
	tuple: Record<string, string[]>
	/**
	 * The object's tuples, when it carries more than the default one. Supplied,
	 * they render as separate assertions and `tuple` is ignored; omitted, the
	 * panel renders the flat `tuple` exactly as before.
	 */
	tuples?: TupleSelection[]
	/** Wires the "add" affordance. Inert (disabled) when omitted. */
	onAddTuple?: () => void
	/** When provided, each assertion gets a remove affordance. */
	onRemoveTuple?: (tupleId: number) => void
	/**
	 * Set when a removal failed. Without it a rejected delete leaves the assertion
	 * on screen with no feedback, which reads as "the click didn't register".
	 */
	removeError?: string | null
	/**
	 * Rendered in place of the "add" button while a new assertion is being
	 * composed. Passed in rather than owned here so the panel stays presentational
	 * — the form needs the axis registry and a mutation, neither of which belongs
	 * in a component that renders from props.
	 */
	addForm?: React.ReactNode
}

/**
 * The file's axis tags rendered as chip groups. An object with several tuples
 * shows one group per assertion (via `TupleChipSet`) rather than a single merged
 * row — two tuples are two independent facts about the file, and merging them
 * would display a pairing nobody asserted.
 *
 * Presentational + storied: it renders from props and never fetches.
 */
export function ClassificationPanel({
	tuple,
	tuples,
	onAddTuple,
	onRemoveTuple,
	removeError = null,
	addForm,
}: ClassificationPanelProps) {
	const grouped = tuples && tuples.length > 0
	const count = grouped
		? tuples.reduce((n, t) => n + Object.values(t.tuple).reduce((m, values) => m + values.length, 0), 0)
		: Object.values(tuple).reduce((n, values) => n + values.length, 0)

	return (
		<SectionCard
			title="Classifications"
			count={count}
			action={
				grouped && tuples.length > 1 ? (
					<p className="text-ds-2xs text-ds-text-subtle" title={ASSERTION_HINT}>
						{tuples.length} assertions
					</p>
				) : null
			}
			className="space-y-3 bg-ds-surface"
		>
			{count > 0 ? (
				grouped ? (
					<TupleChipSet tuples={tuples} onRemoveTuple={onRemoveTuple} />
				) : (
					<ChipSet tuple={tuple} />
				)
			) : (
				<EmptyState title="No classification tags on this file yet." />
			)}
			{removeError ? <p className="text-sm text-ds-danger">{removeError}</p> : null}
			{/* No handler means the caller can't classify (a read-only role), so the
			    affordance is absent rather than permanently greyed out — a disabled
			    button invites a click and explains nothing. */}
			{addForm ?? (onAddTuple ? <AddClassificationButton onPress={onAddTuple} /> : null)}
		</SectionCard>
	)
}

function AddClassificationButton({ onPress }: { onPress: () => void }) {
	return <AddAffordance block label="Add classification" onPress={onPress} />
}
